<template>
  <!-- The demo player's transport: play/pause, the speed ladder, the day steps.
       ONE control for the FAB's mini player and for Settings → Demo → Playback,
       so the two can never word or gate a verb differently (card D2). -->
  <div class="flex flex-wrap items-center gap-2">
    <button
      type="button"
      data-testid="demo-play"
      :disabled="!model.canPlay || pending"
      :title="
        model.playing
          ? tt('Pause the demo clock', 'Ustavi demo uro')
          : tt('Play the demo clock', 'Zaženi demo uro')
      "
      class="flex h-9 items-center gap-1.5 rounded-lg bg-blue-600 px-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600"
      @click="model.playing ? emit('pause') : emit('play')">
      <span class="material-symbols-outlined text-lg">{{ model.playing ? 'pause' : 'play_arrow' }}</span>
      {{ model.playing ? tt('Pause', 'Premor') : tt('Play', 'Predvajaj') }}
    </button>

    <div class="flex h-9 items-center rounded-lg border border-gray-300 dark:border-gray-600">
      <button
        type="button"
        data-testid="demo-slower"
        :disabled="!model.canSpeed || pending"
        :title="tt('Slower', 'Počasneje')"
        class="flex h-full w-8 items-center justify-center rounded-l-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
        @click="emit('speed', stepDemoRate(model.rate, -1))">
        <span class="material-symbols-outlined text-lg">remove</span>
      </button>
      <span
        data-testid="demo-speed"
        class="min-w-[3.25rem] border-x border-gray-300 px-1 text-center text-sm font-semibold tabular-nums text-gray-900 dark:border-gray-600 dark:text-white">
        {{ model.speedLabel }}
      </span>
      <button
        type="button"
        data-testid="demo-faster"
        :disabled="!model.canSpeed || pending"
        :title="tt('Faster', 'Hitreje')"
        class="flex h-full w-8 items-center justify-center rounded-r-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
        @click="emit('speed', stepDemoRate(model.rate, 1))">
        <span class="material-symbols-outlined text-lg">add</span>
      </button>
    </div>

    <div v-if="showDayNav" class="flex h-9 items-center rounded-lg border border-gray-300 dark:border-gray-600">
      <button
        type="button"
        data-testid="demo-prev-day"
        :disabled="!model.canSeek || pending"
        :title="dayNavTitle"
        class="flex h-full w-8 items-center justify-center rounded-l-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
        @click="emit('day', model.dayNumber - 2)">
        <span class="material-symbols-outlined text-lg">skip_previous</span>
      </button>
      <button
        type="button"
        data-testid="demo-next-day"
        :disabled="!model.canSeek || pending"
        :title="dayNavTitle"
        class="flex h-full w-8 items-center justify-center rounded-r-lg text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-300 dark:hover:bg-gray-700"
        @click="emit('day', model.dayNumber)">
        <span class="material-symbols-outlined text-lg">skip_next</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { stepDemoRate, type DemoPlayerModel } from '../../utils/demoPlayerModel';
import { tt } from '../../utils/consoleText';

const props = withDefaults(
  defineProps<{
    model: DemoPlayerModel;
    /** A press is in flight — the hub has not answered yet. */
    pending?: boolean;
    /** Offer the prev/next day steps beside the speed. */
    showDayNav?: boolean;
  }>(),
  { pending: false, showDayNav: true },
);

const emit = defineEmits<{
  (event: 'play'): void;
  (event: 'pause'): void;
  (event: 'speed', rate: number): void;
  /** 0-based day index to jump to. */
  (event: 'day', index: number): void;
}>();

/**
 * A day step needs a hub that can jump days: D0's `/api/demo/playback` answers
 * 400 for `action:'day'` (D8a builds it), so the buttons say what is missing
 * rather than looking broken. `model.canSeek` already carries the admin floor.
 */
const dayNavTitle = computed(() =>
  props.model.canSeek
    ? tt('Jump a day', 'Skoči na drug dan')
    : tt('Day jumps arrive with the timeline', 'Skoki med dnevi pridejo s časovnico'),
);
</script>
